import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { LoginEntityService } from '../../../../../../services/entity-services/login-entity.service';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { Log } from '../../../../../../services/logger.service';

export interface AdhocDialogData {
  classId: string;
  className: string;
  /** Last-used prefix from localStorage; pre-fills the prefix field. */
  lastPrefix?: string;
  /** Current participant loginIds for the class — used to persist the merged list atomically. */
  currentParticipantIds: string[];
  /**
   * Remaining participant slots available for this class (VM quota ∩ ACC subscriptions).
   * When provided, the count input is capped at Min(100, maxCount) and a hint is shown.
   * Omitted when no capacity constraint applies.
   */
  maxCount?: number;
}

export interface AdhocRow {
  seqNo: number;
  loginId: string;
  password: string;
}

@Component({
  selector: 'app-adhoc-participants-dialog',
  templateUrl: './adhoc-participants-dialog.component.html',
  styleUrls: ['./adhoc-participants-dialog.component.css'],
})
export class AdhocParticipantsDialogComponent implements OnInit, OnDestroy {
  prefixCtrl = new FormControl('guest', [
    Validators.required,
    // Letters (a-z) and underscore only; must start and end with a letter.
    Validators.pattern(/^[a-z]([a-z_]*[a-z])?$/),
    // At least 3 letters (underscores excluded from the count).
    (ctrl: AbstractControl): ValidationErrors | null => {
      const letters = (ctrl.value ?? '').replace(/_/g, '').length;
      return letters >= 3 ? null : { minLetters: { required: 3, actual: letters } };
    },
  ]);
  countCtrl = new FormControl<number>(1, [Validators.required, Validators.min(1), Validators.max(100)]);

  prefixInfo: { exists: boolean; currentCount: number } | null = null;
  rows: AdhocRow[] = [];

  /** All prefixes fetched from the server for autocomplete. */
  allPrefixes: { prefix: string; currentCount: number }[] = [];
  /** Filtered subset of allPrefixes shown in the autocomplete drop-down. */
  filteredPrefixes: { prefix: string; currentCount: number }[] = [];

  isCheckingPrefix = false;
  isGenerating = false;

  readonly prefixRulesTooltip =
    'Format: <prefix>  (e.g. adhoc_user)\n\n' +
    '• lowercase a–z letters and _ only\n' +
    '• cannot start or end with _\n' +
    '• min 3 letters (underscores don\'t count)';

  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly logger = Log.create(AdhocParticipantsDialogComponent);

  constructor(
    public readonly dialogRef: MatDialogRef<AdhocParticipantsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AdhocDialogData,
    private readonly loginService: LoginEntityService,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly snackBar: MatSnackBar,
  ) {
    // Pre-fill from last used prefix for this class; empty if none recorded yet.
    const initial = (data.lastPrefix ?? '').trim();
    this.prefixCtrl.setValue(initial);

    // Override the count cap to the lesser of 100 and the remaining participant slots
    // so the operator cannot exceed class capacity from the ad-hoc dialog.
    if (data.maxCount !== undefined && data.maxCount >= 0) {
      const cap = Math.min(100, data.maxCount);
      this.countCtrl.setValidators([
        Validators.required,
        Validators.min(cap > 0 ? 1 : 0),
        Validators.max(cap),
      ]);
      // Clamp the default value down if needed.
      const current = this.countCtrl.value ?? 1;
      this.countCtrl.setValue(Math.min(current, cap));
      this.countCtrl.updateValueAndValidity();
    }
  }

  ngOnInit(): void {
    this.runPrefixCheck();
    // Load all known prefixes for autocomplete.
    this.loginService.getAdHocPrefixes().subscribe({
      next: (list) => {
        this.allPrefixes = list ?? [];
        this.applyPrefixFilter();
      },
      error: () => { this.allPrefixes = []; },
    });
  }

  ngOnDestroy(): void {
    if (this._debounceTimer !== null) clearTimeout(this._debounceTimer);
  }

  onPrefixChange(): void {
    this.prefixInfo = null;
    this.applyPrefixFilter();
    if (this._debounceTimer !== null) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.runPrefixCheck(), 400);
  }

  private applyPrefixFilter(): void {
    const q = (this.prefixCtrl.value ?? '').trim().toLowerCase();
    this.filteredPrefixes = q
      ? this.allPrefixes.filter(p => p.prefix.toLowerCase().includes(q))
      : [...this.allPrefixes];
  }

  private runPrefixCheck(): void {
    const prefix = (this.prefixCtrl.value ?? '').trim();
    if (!prefix || this.prefixCtrl.invalid) { this.prefixInfo = null; return; }
    this.isCheckingPrefix = true;
    this.loginService.checkAdHocPrefix(prefix).subscribe({
      next: info => { this.prefixInfo = info; this.isCheckingPrefix = false; },
      error: () => { this.prefixInfo = null; this.isCheckingPrefix = false; },
    });
  }

  get prefixHint(): string {
    if (this.isCheckingPrefix) return 'Checking…';
    if (!this.prefixInfo) return '';
    return this.prefixInfo.exists
      ? `Resumes from #${this.prefixInfo.currentCount + 1}`
      : 'New sequence';
  }

  /** Effective hard cap on the count input: Min(100, remaining slots). */
  get effectiveMaxCount(): number {
    return this.data.maxCount !== undefined ? Math.min(100, this.data.maxCount) : 100;
  }

  /**
   * Hint shown beneath the count field when a capacity cap is active.
   * Empty when there is no constraint.
   */
  get capacityHint(): string {
    if (this.data.maxCount === undefined) return '';
    const cap = this.effectiveMaxCount;
    return cap === 0
      ? 'No participant slots remaining for this class'
      : `Max ${cap} — limited by class participant capacity`;
  }

  get hasRows(): boolean { return this.rows.length > 0; }
  get canGenerate(): boolean {
    const count = this.countCtrl.value ?? 0;
    return (
      this.prefixCtrl.valid &&
      count >= 1 &&
      count <= this.effectiveMaxCount &&
      !this.isGenerating
    );
  }

  async generateAndAdd(): Promise<void> {
    if (!this.canGenerate) return;
    this.isGenerating = true;
    this.rows = [];

    try {
      const created: any[] = await this.loginService.createAdHocUsers({
        count: this.countCtrl.value,
        prefix: (this.prefixCtrl.value ?? '').trim(),
      });
      this.rows = created.map((item: any, idx: number) => {
        const loginId: string = (item.loginId ?? item.LoginId ?? '').trim();
        if (!loginId) {
          this.logger.e('Ad-hoc API response missing loginId for item at index', idx, item);
        }
        return {
          seqNo: item.details?.seqNo ?? item.details?.SeqNo ?? idx + 1,
          loginId,
          password: item.password
            ?? item.Password
            ?? item.details?.planPassword
            ?? item.details?.PlanPassword
            ?? '',
        };
      });
      this.runPrefixCheck();
      this.logger.i('Generated ad-hoc users:', this.rows.length);

      // Atomically add new participants to the class.
      const newIds = this.rows.map(r => r.loginId);
      const existingSet = new Set((this.data.currentParticipantIds ?? []).map((id: string) => id.toLowerCase()));
      const allIds = [
        ...(this.data.currentParticipantIds ?? []),
        ...newIds.filter(id => !existingSet.has(id.toLowerCase())),
      ];

      this.classService.updateClassPartial(this.data.classId, {
        'TrainingSchedule.Participants': allIds,
      }).subscribe({
        next: () => {
          this.isGenerating = false;
          this.dialogRef.close({
            added: this.rows.map(r => ({ loginId: r.loginId, password: r.password, seqNo: r.seqNo })),
            prefix: (this.prefixCtrl.value ?? '').trim(),
          });
        },
        error: (err: any) => {
          this.isGenerating = false;
          this.logger.e('Add participants to class failed:', err);
          this.snackBar.open(
            'Accounts created but failed to register in class — refresh the page to see them.',
            'Dismiss',
            { duration: 6000 },
          );
          // Return result so parent can update local state even if persist failed.
          this.dialogRef.close({
            added: this.rows.map(r => ({ loginId: r.loginId, password: r.password, seqNo: r.seqNo })),
            prefix: (this.prefixCtrl.value ?? '').trim(),
          });
        },
      });
    } catch (err) {
      this.logger.e('Generate failed:', err);
      this.isGenerating = false;
      this.snackBar.open('Failed to generate ad-hoc participants.', 'Dismiss', { duration: 3000 });
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
